import { verifyPaymentStatus } from '@common/credits/ProfessionalEdition/data/api';
import { usePlans } from '@common/credits/ProfessionalEdition/hooks';
import { PaymentStatus, PaymentStatusResponse, Plan, PLAN_TYPES } from '@common/credits/ProfessionalEdition/types';
import { useProfessionalEditionCreditsContext } from '@common/credits/ProfessionalEdition/useProfessionalEditionCreditsContext';
import { postMessageToOpener } from '@common/oAuth/utils';
import { useIsVerticalApp } from '@common/VerticalApps/utils';
import { WalletBIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { StackItem } from '@prophecy/ui-v3/Layout';
import { Stack } from '@prophecy/ui-v3/Layout/Stack';
import { Spinner } from '@prophecy/ui-v3/Spinner';
import { Text } from '@prophecy/ui-v3/Typography/Text';
import { useRestQuery } from 'frontend/core/src/data/util';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { BillingOverview } from './ProfessionalEdition/components/BillingOverview';
import { CreditUsage } from './ProfessionalEdition/components/CreditUsage';
import { PaymentStatusDialog } from './ProfessionalEdition/components/PaymentStatusDialog';
import { PricingTiers } from './ProfessionalEdition/components/PricingTiers';

export const Billing = () => {
  const {
    meteringInfo: { plan },
    isLoading,
    refetch,
    updateMeteringInfo
  } = useProfessionalEditionCreditsContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [showCheckoutStatusDialog, setShowCheckoutStatusDialog] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>(PaymentStatus.Unpaid);
  const handledSessionIdRef = useRef<string | null>(null);

  const { data: planData } = usePlans();

  const pricingTiersRef = useRef<HTMLDivElement>(null);

  const isVerticalApp = useIsVerticalApp();
  const { data: paymentStatusResponse, error: paymentStatusError } = useRestQuery<PaymentStatusResponse>(
    ['payment-status', sessionId],
    () => verifyPaymentStatus(sessionId as string),
    {
      enabled: isVerticalApp && sessionId !== null
    }
  );

  useEffect(() => {
    if (paymentStatusError) {
      setShowCheckoutStatusDialog(false);
      return;
    }
    if (!sessionId || !paymentStatusResponse?.data.paymentStatus) {
      return;
    }
    if (handledSessionIdRef.current === sessionId) {
      return;
    }
    handledSessionIdRef.current = sessionId;

    const status = paymentStatusResponse.data.paymentStatus;
    setPaymentStatus(status);
    setShowCheckoutStatusDialog(true);

    if (status === PaymentStatus.Paid) {
      updateMeteringInfo({ plan: PLAN_TYPES.Pro });
      void refetch();
      postMessageToOpener('upgrade-success', undefined);
    }
  }, [paymentStatusError, paymentStatusResponse, sessionId, refetch, updateMeteringInfo]);

  const handlePaymentStatusDialogClose = () => {
    // Remove the session_id from the search params
    searchParams.delete('session_id');
    setSearchParams(searchParams);
    setShowCheckoutStatusDialog(false);
    if (paymentStatus === PaymentStatus.Paid) {
      refetch();
    }
  };
  if (isLoading) {
    return <Spinner spinning={true} />;
  }

  const activePlan = planData?.data.find((data) => data.name === plan);

  return (
    <>
      <Stack direction='vertical' align='center' gap={theme.spaces.x40}>
        <Stack direction='horizontal' align={activePlan ? 'space-between' : 'end'}>
          {activePlan && (
            <Stack direction='horizontal' gap={theme.spaces.x12} alignY='center'>
              <WalletIconWrapper>
                <WalletBIcon type='default' />
              </WalletIconWrapper>
              <Text level='md' weight={theme.fontWeight.medium}>
                Billing
              </Text>
            </Stack>
          )}
        </Stack>
        <BillingOverview
          plans={planData?.data || ([] as Plan[])}
          onPlanManage={() => pricingTiersRef.current?.scrollIntoView({ behavior: 'smooth' })}
        />
        {plan && <CreditUsage />}

        <div ref={pricingTiersRef}>
          <PricingTiers plans={planData?.data || ([] as Plan[])} />
        </div>
      </Stack>
      {showCheckoutStatusDialog && (
        <PaymentStatusDialog paymentStatus={paymentStatus} onClose={handlePaymentStatusDialogClose} />
      )}
    </>
  );
};

const WalletIconWrapper = styled(StackItem)`
  background-color: ${theme.colors.grayBlue50};
  padding: ${theme.spaces.x6};
  border-radius: ${theme.radius.x6};
  border: 1px solid ${theme.colors.grayBlue200};
`;
